import { msg } from "@lingui/core/macro";
import { useLingui } from "@lingui/react";
import { ChevronDownIcon } from "lucide-react";
import { useId, useState } from "react";

import { formatPercent } from "@/features/matches";
import { trackEvent } from "@/shared/analytics";
import { cn } from "@/shared/lib";
import { Button } from "@/shared/ui/button";

import type { CatalogBuild } from "../../model/HeroBuilds";
import { BuildItems } from "../BuildItems";
import { CopyBuildIdButton } from "../CopyBuildIdButton";
import { BuildDetails } from "./BuildDetails";

// Сколько первых ключевых предметов сборки показывать.
const SHOWN_ITEMS = 6;

const NUMBER = msg({ id: "builds.card.number", message: "No. {number}" });
const AUTHOR = msg({ id: "builds.card.author", message: "by {author}" });
const PLAYERS = msg({
  id: "builds.card.players",
  message: "{count, plural, one {# player} other {# players}}",
});
const WINS = msg({ id: "builds.card.wins", message: "{share} wins" });
const MORE = msg({ id: "builds.card.more", message: "More" });
const LESS = msg({ id: "builds.card.less", message: "Collapse" });

type CatalogBuildCardProps = {
  build: CatalogBuild;
  rank: number;
};

// Сборка из игры: место в типе, название, номер (по нему её ищут в игре), автор, сколько
// сильных с ней играют и доля их побед, первые ключевые предметы с минутой покупки,
// «Скопировать номер».
// «Подробнее» раскрывает закуп по фазам и прокачку; у каталога, собранного до раскрытия
// сборок, кнопки нет.
export const CatalogBuildCard = ({ build, rank }: CatalogBuildCardProps) => {
  const { _, i18n } = useLingui();
  const detailsId = useId();
  const [open, setOpen] = useState(false);
  const hasDetails = build.purchases.length > 0 || build.ability_order !== null;
  const toggle = () => {
    if (!open) {
      trackEvent("build_expand");
    }
    setOpen(!open);
  };
  const share = formatPercent(build.matches === 0 ? 0 : build.wins / build.matches, i18n.locale);
  const meta = [
    // Номер — по нему сборку ищут в игре; на телефоне его можно набрать руками.
    _({ ...NUMBER, values: { number: String(build.build_id) } }),
    build.author_name === null ? null : _({ ...AUTHOR, values: { author: build.author_name } }),
    _({ ...PLAYERS, values: { count: build.players } }),
    _({ ...WINS, values: { share } }),
  ]
    .filter((part) => part !== null)
    .join(" · ");
  return (
    <li
      data-build-id={build.build_id}
      className={cn(
        "flex gap-3.5 rounded-xl border bg-surface px-4 py-3.5",
        open ? "border-border-strong" : "border-border",
      )}
    >
      <span className="w-5 shrink-0 font-mono text-lg leading-snug font-semibold text-accent">
        {rank}
      </span>
      <div className="flex min-w-0 flex-1 flex-col gap-2.5">
        <div className="flex flex-wrap items-start gap-x-4 gap-y-2">
          <div className="flex min-w-0 flex-[1_1_280px] flex-col gap-0.5">
            <span className="text-[17px] font-semibold [overflow-wrap:anywhere]">{build.name}</span>
            <span className="text-[13px] [overflow-wrap:anywhere] text-subtle">{meta}</span>
          </div>
          <CopyBuildIdButton buildId={build.build_id} />
        </div>
        {open ? (
          <BuildDetails build={build} id={detailsId} />
        ) : (
          <BuildItems
            items={build.items
              .slice(0, SHOWN_ITEMS)
              .map((item) => ({ itemId: item.item_id, atS: item.avg_buy_time_s }))}
          />
        )}
        {hasDetails && (
          <Button
            type="button"
            variant="link"
            aria-expanded={open}
            aria-controls={open ? detailsId : undefined}
            onClick={toggle}
            className="min-h-11 self-start px-0.5 font-semibold text-accent"
          >
            {_(open ? LESS : MORE)}
            <ChevronDownIcon
              aria-hidden="true"
              className={cn("size-4 transition-transform", open && "rotate-180")}
            />
          </Button>
        )}
      </div>
    </li>
  );
};
